import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { UserPlus, Copy, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const EmployeeOnboardingInviteButton = () => {
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', language: 'de' });
  const [link, setLink] = useState('');

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const invite = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) {
      toast.error('Vor- und Nachname sind erforderlich.');
      return;
    }
    if (!form.email.trim() && !form.phone.trim()) {
      toast.error('Bitte E-Mail oder Natel angeben.');
      return;
    }
    setSending(true);
    try {
      const r = await axios.post(`${API}/employee-onboarding/invite`, {
        first_name: form.first_name, last_name: form.last_name,
        email: form.email || null, phone: form.phone || null, role: 'trainer',
        language: form.language,
      }, { headers: headers() });
      setLink(r.data.onboarding_link);
      const sent = r.data.sent || {};
      toast.success(sent.email ? 'Einladung per E-Mail gesendet.' : 'Einladungs-Link erstellt.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Einladung fehlgeschlagen.');
    } finally { setSending(false); }
  };

  const reset = () => { setForm({ first_name: '', last_name: '', email: '', phone: '', language: 'de' }); setLink(''); };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="employee-onboarding-invite-button">
          <UserPlus size={16} className="mr-2" />
          Onboarding einladen
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Mitarbeiter-Onboarding einladen</DialogTitle>
        </DialogHeader>
        {!link ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Der Mitarbeiter erhält einen Link und trägt AHV-Nr., Adresse, Bankverbindung usw. selbst ein.
              Die Daten gehen danach direkt ans Lohnsystem.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Vorname</Label>
                <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="invite-first-name" />
              </div>
              <div className="space-y-1.5">
                <Label>Nachname</Label>
                <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="invite-last-name" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>E-Mail</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="invite-email" />
            </div>
            <div className="space-y-1.5">
              <Label>Natel</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+41 79 …" data-testid="invite-phone" />
            </div>
            <div className="space-y-1.5">
              <Label>Sprache der Einladung</Label>
              <select
                value={form.language}
                onChange={(e) => setForm({ ...form, language: e.target.value })}
                className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                data-testid="invite-language"
              >
                <option value="de">Deutsch</option>
                <option value="en">English</option>
                <option value="fr">Français</option>
                <option value="it">Italiano</option>
                <option value="ru">Русский</option>
                <option value="pt">Português</option>
                <option value="sk">Slovenčina</option>
                <option value="es">Español</option>
              </select>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Einladungs-Link (teilen, falls kein Versand erfolgt ist):</p>
            <div className="flex items-center gap-2">
              <Input readOnly value={link} className="text-xs" data-testid="invite-link-output" />
              <Button type="button" variant="outline" size="icon" onClick={() => { navigator.clipboard.writeText(link); toast.success('Kopiert'); }}>
                <Copy size={16} />
              </Button>
            </div>
          </div>
        )}
        <DialogFooter>
          {!link ? (
            <Button onClick={invite} disabled={sending} data-testid="invite-send-button">
              {sending ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
              {sending ? 'Senden…' : 'Einladung senden'}
            </Button>
          ) : (
            <Button variant="outline" onClick={() => setOpen(false)}>Schliessen</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
